/**
 * 画面の組み立て。入口（ログイン・招待）と本体（アウトライン）を切り替える。
 *
 * 意味論は `@biside/domain`、接続は `sync.ts`、編集は `outline.ts` にある。ここは配線だけ。
 */

import { boardHTML, detailHTML, esc, type BoardCard, type BoardColumn, type CardDetail } from "./board.ts";
import { OutlineStore } from "./store.ts";
import { OutlineView } from "./outline.ts";
import { SyncLink, type LinkState } from "./sync.ts";
import { installCommand, installStepsHTML, isMac, wireInstall } from "./install.ts";
import type { SyncOp } from "@biside/domain";

const $ = <T extends HTMLElement>(sel: string) => document.querySelector<T>(sel)!;

async function api(path: string, init: RequestInit = {}) {
  const res = await fetch(path, { credentials: "same-origin", ...init });
  const text = await res.text();
  return { ok: res.ok, status: res.status, json: text ? JSON.parse(text) : null };
}
const post = (path: string, body: unknown) =>
  api(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

/**
 * 招待トークン。**入口は `/join/<token>`** で、`?invite=` は前に配ったリンクのための退路。
 * 生きているリンクを切らないため、両方を受ける。
 */
const inviteToken =
  location.pathname.match(/^\/join\/([A-Za-z0-9_-]+)/)?.[1] ?? new URLSearchParams(location.search).get("invite");

// ---------------------------------------------------------------------- 参加

/**
 * 参加が済んだ人の行き先。**Web アプリ本体ではない**（まだ出していない面なので、
 * 招かれた人がそこへ落ちると何をすればよいか分からない）。`joined=1` は、着いた先で
 * 「参加しました」と言い切るための印。
 */
const welcomeURL = (workspaceId: string | null) =>
  workspaceId ? `/welcome?ws=${encodeURIComponent(workspaceId)}&joined=1` : "/welcome";

interface Invite {
  email: string;
  workspaceId: string | null;
  workspaceTitle: string;
  invitedBy: string;
}

/**
 * 招待を受ける画面。**ここで参加まで終わる**（アプリは要らない）。
 *
 * 前の版は、招待リンクを踏むと「アカウントを作る」というログイン画面が出るだけだった。
 * 誰が何に呼んでいるのかが画面に無く、一方でメールは「アプリにリンクを貼れ」と言っていた
 * ので、入口が 2 つあるように見えていた。**押した先が終着点**であることを画面で示す。
 */
async function renderJoin(token: string, signedIn: { email: string } | null): Promise<void> {
  $("#app").innerHTML = `<div class="join" id="join"><p class="muted">読み込んでいます…</p></div>`;
  const { ok, json } = await api(`/api/invites/${token}`);
  if (!ok) {
    $("#join").innerHTML = `
      <h1 class="join-title">招待が使えません</h1>
      <p class="muted">期限が切れているか、すでに使われています。<br>招いた人にもう一度送ってもらってください。</p>`;
    return;
  }
  const inv = json as Invite;

  // **誰が・どこへ呼んでいるかを最初に出す。** 招待を受けた人向けの画面は「新規登録」とは
  // 別物として作る（招かれた人はまだ製品を知らないので、フォームより先に文脈が要る）。
  // 画面の目的は 1 つ、押すものも 1 つ。
  const head = `
    <div class="join-avatar" aria-hidden="true">${esc([...inv.workspaceTitle][0] ?? "B")}</div>
    <p class="join-from">${esc(inv.invitedBy)} さんが招待しています</p>
    <h1 class="join-title">${esc(inv.workspaceTitle)}</h1>`;

  // 既に入っている人が招待を踏んだとき。**もう一度アカウントを作らせない。**
  if (signedIn) {
    const same = signedIn.email.toLowerCase() === inv.email.toLowerCase();
    $("#join").innerHTML = `
      ${head}
      ${same
        ? `<p class="join-as">${esc(signedIn.email)} として参加します</p>
           <button class="join-primary" id="accept">参加する</button>`
        : `<p class="join-as warn">この招待は <strong>${esc(inv.email)}</strong> 宛です。<br>
             いまは ${esc(signedIn.email)} で入っています。</p>
           <button class="join-primary" id="out">ログアウトして開き直す</button>`}
      <div class="msg" id="msg"></div>
      <p class="join-fine">リンクの有効期限は 7 日です。</p>`;
    if (same) {
      $("#accept").addEventListener("click", async () => {
        const btn = $<HTMLButtonElement>("#accept");
        btn.disabled = true;
        const res = await post(`/api/invites/${token}/accept`, {});
        if (res.ok) { location.href = welcomeURL(res.json.workspaceId); return; }
        btn.disabled = false;
        $("#msg").textContent = res.json?.error ?? "参加できませんでした";
      });
    } else {
      $("#out").addEventListener("click", async () => {
        await post("/api/auth/sign-out", {});
        location.reload();
      });
    }
    return;
  }

  $("#join").innerHTML = `
    ${head}
    <p class="join-as">${esc(inv.email)} として参加します</p>
    <form id="joinForm">
      <label class="join-field">お名前
        <input name="name" placeholder="山田 太郎" autocomplete="name" required>
      </label>
      <label class="join-field">パスワード
        <input name="password" type="password" placeholder="10 文字以上" autocomplete="new-password"
               minlength="10" required>
      </label>
      <button class="join-primary" type="submit">参加する</button>
      <div class="msg" id="msg"></div>
    </form>
    <p class="join-fine">ここで決めた名前とパスワードが、そのままアカウントになります。<br>
      リンクの有効期限は 7 日です。</p>`;

  const form = $<HTMLFormElement>("#joinForm");
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const btn = form.querySelector<HTMLButtonElement>(".join-primary")!;
    btn.disabled = true;
    const f = Object.fromEntries(new FormData(form) as any) as Record<string, string>;
    const res = await post("/api/auth/sign-up/email", {
      email: inv.email, password: f.password, name: f.name, inviteToken: token,
    });
    if (res.ok) {
      location.href = welcomeURL(inv.workspaceId);
      return;
    }
    // すでにそのメールのアカウントがある場合。**作れないのではなく、入ればいい。**
    const signIn = await post("/api/auth/sign-in/email", { email: inv.email, password: f.password });
    if (signIn.ok) {
      const accepted = await post(`/api/invites/${token}/accept`, {});
      location.href = welcomeURL(accepted.ok ? accepted.json.workspaceId : null);
      return;
    }
    btn.disabled = false;
    $("#msg").textContent =
      "参加できませんでした。このメールのアカウントが既にある場合は、そのときのパスワードを入れてください。";
  });
}

/**
 * Mac アプリからの「サインインさせて」を受ける画面（`/link?challenge=…&state=…&scheme=…`）。
 *
 * **アプリにパスワードを打たせないための面。** ここで人がすることは「許可する」を 1 回押すだけで、
 * 押した結果は私用スキームでアプリへ戻る（RFC 8252 と同じ形）。合言葉そのものは渡らない。
 */
async function renderLink(signedIn: { email: string } | null): Promise<void> {
  const q = new URLSearchParams(location.search);
  const challenge = q.get("challenge") ?? "";
  const state = q.get("state") ?? "";
  const scheme = q.get("scheme") ?? "biside";
  if (!challenge) {
    $("#app").innerHTML = `<div class="join"><h1>この画面は Mac アプリから開きます</h1></div>`;
    return;
  }
  if (!signedIn) {
    // 入っていなければ、まず入ってもらう。**戻り先はこの画面**なので、入ったあとに続きへ進める。
    renderGate("signin");
    return;
  }
  // **押す前に「何ができるようになるか」を言う。** 前は「この Mac だけの身分証が渡ります」と
  // 仕組みだけを書いていて、押した結果どうなるのかが読み取れなかった（見出しも疑問文なのに
  // 「いいえ」が無く、確認の意味が伝わらない）。文言は Mac の設定「コラボレーション」と
  // 揃えてある —— さっき読んだ文がそのまま出れば、続きだと分かる。
  const device = q.get("device")?.trim() ?? "";
  $("#app").innerHTML = `
    <div class="join">
      <h1 class="join-title">この Mac でコラボレーションを<br>有効にします</h1>
      <p class="join-as">${esc(signedIn.email)}</p>
      ${device ? `<p class="join-device">${esc(device)}</p>` : ""}
      <p class="join-lead">ワークスペース毎に参加者を招待し、共同編集できるようになります。</p>
      <form id="linkForm"><button class="join-primary" type="submit">有効にする</button></form>
      <button class="join-cancel" id="cancel">キャンセル</button>
      <div class="msg" id="msg"></div>
      <p class="join-fine">参加中のワークスペースは、この Mac に取り込まれます。
        渡すのはこの Mac だけの身分証で、パスワードは渡りません。
        設定 →「コラボレーション」からいつでもサインアウトできます。</p>
    </div>`;
  // 断ったときは何も起こさない。**ブラウザのタブを勝手に閉じない**（script で開いた窓しか
  // 閉じられないので、閉じたつもりで閉じない、という一番悪い形になる）。
  $("#cancel").addEventListener("click", () => {
    $("#app").innerHTML = `<div class="join"><p class="muted">有効にしませんでした。この画面は閉じてかまいません。</p></div>`;
  });
  $<HTMLFormElement>("#linkForm").addEventListener("submit", async (e) => {
    e.preventDefault();
    const btn = $<HTMLButtonElement>("#linkForm button");
    btn.disabled = true;
    const res = await post("/api/device/authorize", { challenge });
    if (!res.ok) {
      btn.disabled = false;
      $("#msg").textContent = res.json?.error ?? "うまくいきませんでした";
      return;
    }
    // 戻る先はアプリ。**ここから先はブラウザの仕事ではない。**
    const back = `${scheme}://auth?code=${encodeURIComponent(res.json.code)}&state=${encodeURIComponent(state)}`;
    location.href = back;

    // **終わったことが分かる画面に差し替える。**
    //
    // 前は、押したフォームをそのまま残して下に赤い 1 行（`.msg` は入口のエラー色）を足していた。
    // 成功なのに失敗のように読め、しかもボタンが押せるままなので、もう一度押した人が居た
    // （実測）。引換券は 1 度きりなので、2 回目は必ず失敗する —— つまり**この画面に
    // 「もう一度押す」余地を残すこと自体が誤り**だった。
    //
    // 手で開く道は残す: 私用スキームは環境によっては黙って無視される（既定のブラウザや
    // 企業の設定で開かないことがある）ので、そのときの逃げ道が無いと詰む。
    $("#app").innerHTML = `
      <div class="join">
        <h1 class="join-title">この Mac を有効にしました</h1>
        <p class="join-lead">Biside に戻ってください。<br>この画面は閉じてかまいません。</p>
        <a class="join-primary" href="${back}">Biside を開く</a>
        <p class="join-fine">アプリが前に出てこないときは、上のボタンを押してください。
          それでも変わらないときは、Biside の設定 →「コラボレーション」を開き直してください。</p>
      </div>`;
  });
}

// ---------------------------------------------------------------------- 入口

function renderGate(mode: "signin" | "signup", bootstrap = false): void {
  const signUp = mode === "signup";
  // **この画面へ来た理由を出す。** Mac の「ブラウザでサインイン」から来たときは、
  // ログインは目的ではなく途中の 1 手なので、それを言わないと何の画面か読めない。
  const fromMac = location.pathname === "/link";
  $("#app").innerHTML = `
    <div class="join">
      <h1 class="join-title">${signUp ? "アカウントを作る" : "Biside にサインイン"}</h1>
      ${fromMac ? `<p class="join-lead">この Mac を接続するために、サインインします。</p>` : ""}
      ${signUp && bootstrap ? `<p class="join-as">まだ誰も居ません。<strong>最初の 1 人</strong>として作ります（以降は招待が要ります）</p>` : ""}
      <!-- 外部ログイン（Google など）を足すときは、ここに .join-providers と .join-or を出す。
           骨格だけ用意してあるので、増えるのはボタンの塊 1 つだけで済む。 -->
      <form id="gate">
        ${signUp ? `<label class="join-field">名前
          <input name="name" autocomplete="name" enterkeyhint="next">
        </label>` : ""}
        <label class="join-field">メール
          <input name="email" type="email" autocomplete="username" autocapitalize="off"
                 spellcheck="false" enterkeyhint="next" required autofocus>
        </label>
        <label class="join-field">パスワード
          <input name="password" type="password" enterkeyhint="go" required
                 autocomplete="${signUp ? "new-password" : "current-password"}">
          ${signUp ? `<small class="join-hint">10 文字以上</small>` : ""}
        </label>
        <button class="join-primary" type="submit">${signUp ? "作る" : "サインイン"}</button>
      </form>
      <div class="msg" id="msg" role="alert"></div>
      ${signUp ? "" : `<p class="join-links"><a href="/forgot-password">パスワードをお忘れですか</a></p>`}
      <p class="join-fine">${signUp
        ? "このアカウントで、あとから人を招待できます。"
        : "アカウントは招待メールから作られます。招待を受け取った方は、メールのリンクを開いてください。"}</p>
    </div>`;

  // **ここに招待は来ない。** 招待があるときは起動時に `renderJoin` へ回すので、
  // この画面は「サインイン」と「最初の 1 人」だけを相手にする。
  const form = $<HTMLFormElement>("#gate");

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const btn = $<HTMLButtonElement>("#gate button");
    btn.disabled = true;
    const body: Record<string, unknown> = Object.fromEntries(new FormData(form) as any);
    const { ok, json } = await post(signUp ? "/api/auth/sign-up/email" : "/api/auth/sign-in/email", body);
    if (ok) {
      // **来た道へ戻す。** `/link?challenge=…` から来たのに `/` へ飛ばしていたので、
      // 許可の画面へ進めず、Mac は身分証を受け取れないまま待っていた（実測）。
      if (fromMac || location.pathname === "/app") location.reload();
      else location.href = "/welcome";
      return;
    }
    btn.disabled = false;
    $("#msg").textContent = json?.error ?? json?.message ?? "うまくいきませんでした";
  });
}

/**
 * パスワードを忘れた人の入口。
 *
 * **アカウントの有無を漏らさない。** 送っても送らなくても同じ文言を返す（サーバも同じ作りで、
 * `/request-password-reset` は常に同じ応答を返す）。ここで「そのメールはありません」と
 * 言ってしまうと、誰が使っているかを外から確かめられる。
 */
function renderForgot(): void {
  $("#app").innerHTML = `
    <div class="join">
      <h1 class="join-title">パスワードを再設定します</h1>
      <p class="join-lead">登録しているメールに、再設定のリンクを送ります。</p>
      <form id="forgot">
        <label class="join-field">メール
          <input name="email" type="email" autocomplete="username" autocapitalize="off"
                 spellcheck="false" enterkeyhint="go" required autofocus>
        </label>
        <button class="join-primary" type="submit">再設定のリンクを送る</button>
      </form>
      <div class="msg" id="msg" role="alert"></div>
      <p class="join-links"><a href="/">サインインに戻る</a></p>
    </div>`;
  const form = $<HTMLFormElement>("#forgot");
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const btn = $<HTMLButtonElement>("#forgot button");
    btn.disabled = true;
    const email = String(new FormData(form).get("email") ?? "");
    await post("/api/auth/request-password-reset", { email, redirectTo: `${location.origin}/reset-password` });
    $("#app").innerHTML = `
      <div class="join">
        <h1 class="join-title">送りました</h1>
        <p class="join-lead">そのアドレスで登録があれば、再設定のリンクが届きます。<br>
          リンクは 1 時間で切れます。</p>
        <p class="join-links"><a href="/">サインインに戻る</a></p>
      </div>`;
  });
}

/** 再設定のリンクを開いた先。`?token=…`（有効）か `?error=…`（切れている）で来る。 */
function renderReset(): void {
  const q = new URLSearchParams(location.search);
  const token = q.get("token") ?? "";
  if (!token) {
    $("#app").innerHTML = `
      <div class="join">
        <h1 class="join-title">このリンクは使えません</h1>
        <p class="join-lead">期限が切れているか、すでに使われています。もう一度送り直してください。</p>
        <a class="join-primary" href="/forgot-password">再設定のリンクを送る</a>
      </div>`;
    return;
  }
  $("#app").innerHTML = `
    <div class="join">
      <h1 class="join-title">新しいパスワードを決めます</h1>
      <form id="reset">
        <label class="join-field">新しいパスワード
          <input name="newPassword" type="password" autocomplete="new-password"
                 enterkeyhint="go" required autofocus>
          <small class="join-hint">10 文字以上</small>
        </label>
        <button class="join-primary" type="submit">決める</button>
      </form>
      <div class="msg" id="msg" role="alert"></div>
    </div>`;
  const form = $<HTMLFormElement>("#reset");
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const btn = $<HTMLButtonElement>("#reset button");
    btn.disabled = true;
    const newPassword = String(new FormData(form).get("newPassword") ?? "");
    const { ok, json } = await post("/api/auth/reset-password", { newPassword, token });
    if (ok) { location.href = "/"; return; }
    btn.disabled = false;
    $("#msg").textContent = json?.error ?? json?.message ?? "うまくいきませんでした";
  });
}

// ---------------------------------------------------------------------- 着地点

interface Workspace {
  id: string;
  title: string;
}

/**
 * どのアプリを開くか。**staging の画面から本番のアプリを開かせない**（逆も同じ）。
 * スキームは本番が `biside`、staging が `biside-staging`（`project.yml` の `info:`）。
 * 手元の検証用（slot 付き）だけは `?scheme=` で名指しできるが、形は検めてから使う。
 */
function appScheme(): string {
  const asked = new URLSearchParams(location.search).get("scheme") ?? "";
  if (/^biside(-staging(-slot\d+)?)?$/.test(asked)) return asked;
  return location.hostname.startsWith("staging.") ? "biside-staging" : "biside";
}

const openAppURL = (workspaceId: string) =>
  `${appScheme()}://workspace/${workspaceId}?server=${encodeURIComponent(location.origin)}`;

/**
 * 参加が済んだ人の着地点（`/welcome`）。**ここが Web の終点**で、続きは Mac アプリ。
 *
 * 前は参加のあと Web アプリ本体（ボードとノート）へ飛ばしていた。あれはまだ出していない面で、
 * 招かれた人には「何をすればいいのか」が 1 つも書いていない —— しかも本命の Mac アプリは
 * 画面の隅に小さく出るだけだった。**目的は 1 つ、押すものも 1 つ**にする。
 *
 * 入っていない人のために、入れ方は**畳まずに**同じ画面へ置く（`install.ts`）。私用スキームは
 * アプリが無いと黙って何も起きない（Safari は逆にエラーを出す）ので、押して駄目だったときの
 * 続きが同じ画面に無いと、そこで行き止まりになる。
 */
async function renderWelcome(user: { email: string }, workspaces: Workspace[]): Promise<void> {
  const q = new URLSearchParams(location.search);
  const joined = q.get("joined") === "1";
  // **1 枚に 1 つだけ。** ほかのワークスペースは並べない —— 開いたあとはアプリ側が参加中の
  // ものを全部取り込む（`bringDownJoinedWorkspaces`）ので、ここに一覧を置く用事が無い。
  // 置いていた頃は、それだけで画面が「読み飛ばす対象」に見えた（2026-08-22 の指摘）。
  const focus = workspaces.find((w) => w.id === q.get("ws")) ?? workspaces[0]!;

  const head = `
    <div class="join-avatar" aria-hidden="true">${esc([...focus.title][0] ?? "B")}</div>
    <h1 class="join-title">${joined ? `「${esc(focus.title)}」に参加しました` : esc(focus.title)}</h1>
    <p class="join-as">${esc(user.email)}</p>`;

  // **Mac 以外には、無い道を勧めない。** 参加そのものは済んでいるので、やり直しは要らない。
  if (!isMac()) {
    $("#app").innerHTML = `
      <div class="join">
        ${head}
        <p class="join-lead">Biside はいま <strong>macOS 用のアプリ</strong>です。<br>
          Mac でこのページを開くと、続きから始められます。</p>
        <p class="join-fine">アカウントはもうできています。Mac から
          <strong>${esc(location.host)}</strong> を開き、${esc(user.email)} でサインインしてください。</p>
      </div>`;
    return;
  }

  const cmd = await installCommand();
  $("#app").innerHTML = `
    <div class="join">
      ${head}
      <p class="join-lead">続きは Mac アプリで。</p>
      <a class="join-primary" id="openApp" href="${openAppURL(focus.id)}">Biside で開く</a>
      <p class="wel-hint">クリックしても何も起きなければ、<br>
        Mac にまだ Biside がインストールされていません。</p>
      ${installStepsHTML(cmd)}
    </div>`;
  wireInstall();
}

// ---------------------------------------------------------------------- 本体（Web アプリ）
//
// **まだ出していない面。** リリースの目処が立つまで `/app` に置き、どこからもリンクしない。
// 招かれた人がここへ落ちると「何をすればいいのか分からない画面」になるため（それが理由の
// すべてで、中身は凍結してある）。開発を再開するときは、起動の分岐を 1 行戻せばよい。

function renderApp(user: { email: string }, workspaces: Workspace[]): void {
  const workspaceId = new URLSearchParams(location.search).get("ws") ?? workspaces[0]!.id;

  $("#app").innerHTML = `
    <header>
      <strong>Biside</strong>
      <select id="wsPick">${workspaces
        .map((w) => `<option value="${w.id}" ${w.id === workspaceId ? "selected" : ""}>${w.title}</option>`)
        .join("")}</select>
      <div class="tabs">
        <button id="tabBoard" class="tab on">ボード</button>
        <button id="tabNote" class="tab">ノート</button>
      </div>
      <nav id="crumbs"></nav>
      <span id="who">${user.email}</span>
      <button id="newWs">＋ ワークスペース</button>
      <button id="invite">招待</button>
      <button id="out">ログアウト</button>
      <span id="state" class="down">未接続</span>
    </header>
    <main>
      <div id="board" class="board"></div>
      <div id="note" hidden>
        <div id="rows" class="outline"></div>
        <button id="add" class="add">＋ 行を足す</button>
      </div>
      <div id="inviteBox"></div>
      <!-- **ID を人に運ばせない。** 前はここに「この ID を Mac の設定に貼れ」と出していたが、
           いまは招待リンクだけで繋がる。代わりに置くのは「アプリで開く」への入口。 -->
      <div class="invite" id="macBox"></div>
    </main>`;

  const store = new OutlineStore();
  const link = new SyncLink(workspaceId, store, (state: LinkState, detail) => {
    const el = $("#state");
    el.className = state;
    el.textContent = detail;
  });

  const view = new OutlineView($("#rows"), {
    store,
    send: (ops: SyncOp[]) => link.send(store.local(ops)),
    onZoom: (nodeId) => renderCrumbs(nodeId),
    onMove: (nodeId) => link.tellPresence(nodeId),
    presence: () => link.presence,
  });
  link.onPresence = () => view.render();

  function renderCrumbs(nodeId: string | null): void {
    const trail: { id: string | null; label: string }[] = [{ id: null, label: "すべて" }];
    let cur = nodeId;
    const stack: { id: string; label: string }[] = [];
    while (cur) {
      const n = store.tree.get(cur);
      if (!n) break;
      stack.unshift({ id: n.id, label: n.text || "（無題）" });
      cur = n.parentId;
    }
    trail.push(...stack);
    const nav = $("#crumbs");
    nav.innerHTML = nodeId === null ? "" : trail.map((t) => `<a href="#" data-id="${t.id ?? ""}">${t.label}</a>`).join("<i>›</i>");
    nav.querySelectorAll<HTMLAnchorElement>("a").forEach((a) =>
      a.addEventListener("click", (e) => {
        e.preventDefault();
        view.zoomTo(a.dataset.id || null);
      }),
    );
  }

  $("#wsPick").addEventListener("change", (e) => {
    location.search = `?ws=${(e.target as HTMLSelectElement).value}`;
  });
  $("#out").addEventListener("click", async () => {
    await post("/api/auth/sign-out", {});
    location.href = "/";
  });
  $("#add").addEventListener("click", () => view.appendRow());
  $("#newWs").addEventListener("click", async () => {
    const title = prompt("新しいワークスペースの名前", "共有ワークスペース");
    if (!title) return;
    const { ok, json } = await post("/api/workspaces", { title });
    if (ok) location.href = `/?ws=${json.id}`;
  });

  $("#invite").addEventListener("click", async () => {
    const email = prompt("招待する相手のメール");
    if (!email) return;
    const { ok, json } = await post(`/api/workspaces/${workspaceId}/invites`, { email });
    $("#inviteBox").innerHTML = ok
      ? `<div class="invite">${email} に渡すリンク（7 日間有効）:<br>${json.url}</div>`
      : `<div class="invite">招待を作れませんでした</div>`;
  });

  // ---- ボード（読むための面。招かれた人が最初に見るのはここ） ----
  let openCardID: string | null = null;

  async function loadBoard(): Promise<void> {
    const { ok, json } = await api(`/api/workspaces/${workspaceId}/board`);
    if (!ok) return;
    const { columns, cards } = json as { columns: BoardColumn[]; cards: BoardCard[] };
    $("#board").innerHTML = boardHTML(columns, cards);
    $("#board").querySelectorAll<HTMLButtonElement>("[data-card]").forEach((b) =>
      b.addEventListener("click", () => openCard(b.dataset.card!)),
    );
  }

  async function openCard(nodeId: string): Promise<void> {
    openCardID = nodeId;
    const panel = $("#detail");
    panel.hidden = false;
    panel.innerHTML = `<p class="muted">読み込んでいます…</p>`;
    const { ok, json } = await api(`/api/workspaces/${workspaceId}/card/${encodeURIComponent(nodeId)}`);
    if (!ok) {
      panel.innerHTML = `<p class="muted">開けませんでした。</p>`;
      return;
    }
    panel.innerHTML = detailHTML(json as CardDetail);
    panel.querySelector<HTMLButtonElement>("[data-close]")?.addEventListener("click", () => {
      panel.hidden = true;
      openCardID = null;
    });
  }

  document.body.insertAdjacentHTML("beforeend", `<aside id="detail" class="detail" hidden></aside>`);

  const showBoard = (on: boolean) => {
    $("#board").hidden = !on;
    $("#note").hidden = on;
    $("#tabBoard").classList.toggle("on", on);
    $("#tabNote").classList.toggle("on", !on);
    if (on) void loadBoard();
  };
  $("#tabBoard").addEventListener("click", () => showBoard(true));
  $("#tabNote").addEventListener("click", () => {
    // ノートへ移ったらカードは閉じる（開いたままだと、見えない面を読み直し続ける）。
    openCardID = null;
    $("#detail").hidden = true;
    showBoard(false);
  });
  void loadBoard();

  // **相手の Mac で動いた結果が、黙って古いまま残らないように。** 行の同期とは別口で
  // 畳んだ面を読んでいるので、届いた変更に合わせて読み直す（開いているカードも含めて）。
  let pending: number | undefined;
  const refreshSoon = () => {
    clearTimeout(pending);
    pending = setTimeout(() => {
      if (!$("#board").hidden) void loadBoard();
      if (openCardID) void openCard(openCardID);
    }, 600) as unknown as number;
  };
  // **定期の読み直しはしない。** 届いたら読み直す（`store.onChange`）で足りる。
  // 6 秒ごとに叩いていた頃は、誰も編集していない裏のタブでも全表スキャンを 600 回/時 起こしていた。
  store.onChange(refreshSoon);
  window.addEventListener("focus", refreshSoon);

  // Mac アプリへの受け渡し。入っていれば開き、入っていなければ入れ方を出す。
  void renderMacBox(workspaceId);

  // 検証用の覗き口。画面から読めない「送ったか」「木がどうなっているか」を台本が確かめられる
  // ようにしておく。ここが無いと、失敗したときに DOM から推測するしかなくなる。
  (window as unknown as { __biside: unknown }).__biside = { store, view, link };

  link.start();
}

/**
 * 「Mac アプリで開く」。**入れ方の文面は `install.ts` の 1 枚だけ**を使う（写しを作らない）。
 * 押した先は私用スキームで、入っていなければ何も起きない——だから入れ方も同じ場所に出す。
 */
async function renderMacBox(workspaceId: string): Promise<void> {
  const box = $("#macBox");
  // **接続先も一緒に渡す。** 押した人の Mac は、たいてい何も設定されていない
  // （参加はブラウザで済ませていて、アプリは入れたばかり）。これが無いと、開いた先で
  // どこへ訊けばよいか分からないまま空の画面が出る。
  box.innerHTML = `
    <a class="join-primary" href="${openAppURL(workspaceId)}">Biside で開く</a>
    ${installStepsHTML(await installCommand())}`;
  wireInstall(box);
}

// ---------------------------------------------------------------------- 起動

const session = await api("/api/auth/get-session");
if (location.pathname === "/link") {
  await renderLink(session.json?.user ?? null);
} else if (location.pathname === "/forgot-password") {
  renderForgot();
} else if (location.pathname === "/reset-password") {
  renderReset();
} else if (inviteToken) {
  // **招待があるときは、入っていてもいなくても参加の画面。** ログイン画面に but 書きを
  // 足す形にすると、何のリンクを踏んだのか読み取れない。
  await renderJoin(inviteToken, session.json?.user ?? null);
} else if (!session.json?.user) {
  const bootstrap = (await api("/api/bootstrap")).json?.available === true;
  renderGate(bootstrap ? "signup" : "signin", bootstrap);
} else {
  const { json } = await api("/api/workspaces");
  if (!json.workspaces?.length) {
    await post("/api/workspaces", { title: "はじめのワークスペース" });
    location.reload();
  } else if (location.pathname === "/app") {
    // **Web アプリ本体はここだけ。** どこからもリンクしない（開発者が直打ちで開く面）。
    renderApp(session.json.user, json.workspaces);
  } else {
    // `/` も `/welcome` も、行き着く先は同じ 1 枚。
    await renderWelcome(session.json.user, json.workspaces);
  }
}
